import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useMemo } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useNavigate, useOutletContext, useParams } from 'react-router';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Info } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input as UIInput } from '@/components/ui/input';
import { toast } from '@/components/ui/use-toast';
import { AgentAuthorityContext } from '@/lib/layout/AgentAuthorityContext';
import { GoogleInput } from '@/lib/pages/forms/components/AutocompleteInput';
import { Checkbox } from '@/lib/pages/forms/components/Checkbox';
import { DateInput as DateField } from '@/lib/pages/forms/components/DateInput';
import { Input } from '@/lib/pages/forms/components/Input';
import { PhoneInput } from '@/lib/pages/forms/components/Phone';
import { Select } from '@/lib/pages/forms/components/Select';
import { ZipInput } from '@/lib/pages/forms/components/Zip';
import {
  SelectablePracticeSpecialty,
  SelectableUnitedStatesNewGrad,
  SelectableYesNo,
} from '@/lib/pages/forms/fields';
import type { ParsedGooglePlaceResult } from '@/lib/pages/forms/helpers';

import {
  glacierNewGradApiGetAnswersOptions,
  glacierNewGradApiGetAnswersQueryKey,
  glacierNewGradApiSaveAnswersMutation,
} from '@/lib/heyapi/@tanstack/react-query.gen';
import type {
  GlacierNewGradApiValidateAssociationCodeError,
  UsStateEnum,
  ValidatedAssociationCodeResponse,
} from '@/lib/heyapi/types.gen';
import { YesNoEnum } from '@/lib/heyapi/types.gen';

import type { AgencyType } from '../newGradLayout';
import type { YourInformationForm } from './types';
import {
  DefaultValues,
  detailsBackToFront,
  detailsFrontToBack,
  isRequired,
  selectableMonth,
} from './types';

const NewGrad = () => {
  const { pk, agency_id: paramAgencyId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    agencyData,
    setAgencyData,
    validateAgency,
    validateAssociationCode,
    validatedAssociationCode,
    setValidatedAssociationCode,
  } = useOutletContext<{
    agencyData: {
      name: string;
      phone: string;
      email: string;
      agencyUuid: string;
      agencyOperatingStates: UsStateEnum[];
    };
    setAgencyData: Dispatch<SetStateAction<AgencyType>>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- mutation result type too complex to inline
    validateAgency: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- mutation result type too complex to inline
    validateAssociationCode: any;
    validatedAssociationCode: ValidatedAssociationCodeResponse | null;
    setValidatedAssociationCode: Dispatch<
      SetStateAction<ValidatedAssociationCodeResponse | null>
    >;
  }>();

  const { data } = useQuery({
    ...glacierNewGradApiGetAnswersOptions({
      path: {
        account_id: pk!,
      },
    }),
    enabled: Boolean(pk),
  });

  const answersQK = glacierNewGradApiGetAnswersQueryKey({
    path: {
      account_id: pk!,
    },
  });

  const form = useForm<YourInformationForm>({
    defaultValues: DefaultValues,
    values: detailsBackToFront(data?.pages.yourInformation),
    mode: 'onBlur',
  });

  const selectableStates = useMemo(() => {
    if (
      paramAgencyId === agencyData.agencyUuid &&
      agencyData.agencyOperatingStates?.length
    ) {
      // subset of SelectableUnitedStatesNewGrad with agency operating states
      return SelectableUnitedStatesNewGrad.filter((state) =>
        agencyData.agencyOperatingStates.includes(state.value)
      );
    }

    return SelectableUnitedStatesNewGrad;
  }, [paramAgencyId, agencyData.agencyUuid, agencyData.agencyOperatingStates]);

  const {
    watch,
    control,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = form;

  const watchPolicyContractState = watch('policyContractState');
  const watchFirstTimePractice = watch('firstTimePractice');
  const watchAssociationCode = watch('associationCode');

  const getAssociationCodeErrorMessage = (
    error: GlacierNewGradApiValidateAssociationCodeError | unknown
  ) => {
    if (!error || typeof error !== 'object') {
      return 'Please enter a valid association code or clear the field.';
    }

    if (
      'error' in error &&
      error.error &&
      typeof error.error === 'object' &&
      'message' in error.error &&
      typeof error.error.message === 'string'
    ) {
      return error.error.message;
    }

    if ('message' in error && typeof error.message === 'string') {
      return error.message;
    }

    return 'Please enter a valid association code or clear the field.';
  };

  const canValidateAssociationCode = Boolean(watchPolicyContractState);

  const revertAgencyDisplay = (
    practiceState: UsStateEnum | '' | null | undefined
  ) => {
    const agencyId =
      paramAgencyId || data?.pages.yourInformation.agencyUuid || null;
    const normalizedState = practiceState || null;
    if (normalizedState) {
      validateAgency.mutate({
        body: {
          agencyUuid: agencyId,
          practiceState: normalizedState,
        },
      });
    }
  };

  const validateAssociationCodeValue = async (
    rawCode: string,
    options?: { practiceStateOverride?: UsStateEnum | null }
  ) => {
    const code = rawCode.trim().toUpperCase();
    const practiceState =
      options && 'practiceStateOverride' in options
        ? (options.practiceStateOverride ?? null)
        : watchPolicyContractState || null;
    const agencyUuid =
      paramAgencyId || data?.pages.yourInformation.agencyUuid || null;

    if (!code) {
      setValidatedAssociationCode(null);
      clearErrors('associationCode');
      return true;
    }

    if (!practiceState) {
      setValidatedAssociationCode(null);
      return false;
    }

    try {
      const result = await validateAssociationCode.mutateAsync({
        body: {
          agencyUuid,
          practiceState,
          code,
        },
      });

      setValidatedAssociationCode(result);
      setAgencyData({
        name: result.agencyName || '',
        firstName: result.firstName || '',
        lastName: result.lastName || '',
        phone: result.phone || '',
        email: result.email || '',
        agencyUuid: result.agencyUuid || '',
        agencyOperatingStates: result.agencyOperatingStates,
      });
      clearErrors('associationCode');

      if (code !== watchAssociationCode) {
        setValue('associationCode', code, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
      }

      return true;
    } catch (error) {
      setValidatedAssociationCode(null);
      setError('associationCode', {
        type: 'server',
        message: getAssociationCodeErrorMessage(error),
      });
      revertAgencyDisplay(practiceState);
      return false;
    }
  };

  const saveAnswers = useMutation({
    ...glacierNewGradApiSaveAnswersMutation(),
    onError: (error) => {
      toast({
        title: 'Error saving Policy Details',
        description: (
          <p className="mt-2 max-h-[40vh] w-[340px] rounded-md bg-red-600 p-4 text-white">
            {JSON.stringify(error)}
          </p>
        ),
      });
    },
    onSuccess(_data, _variables, _context) {
      queryClient.refetchQueries({ queryKey: answersQK });

      if (_data.accountId) {
        navigate(
          `/newgrad${_data.accountId ? `/${_data.accountId}` : ''}/2/${_data.pages.yourInformation.agencyUuid || ''}`
        );
      }

      toast({
        title: 'Success',
        description: (
          <pre className="mt-2 w-[340px] rounded-md bg-slate-950 p-4">
            <code className="text-white">Changes saved successfully</code>
          </pre>
        ),
      });
    },
  });

  useEffect(() => {
    setValue('agencyName', agencyData?.name);
    setValue('agencyUuid', agencyData?.agencyUuid);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: review deps in future refactor
  }, [agencyData]);

  const handleSelectPracticeAddress = (address: ParsedGooglePlaceResult) => {
    const dirtyObj = {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    };

    if (address.line1) {
      setValue('primaryPracticeAddress.line1', address.line1, dirtyObj);
    }

    if (address.line2) {
      setValue('primaryPracticeAddress.line2', address.line2, dirtyObj);
    }

    if (address.city) {
      setValue('primaryPracticeAddress.city', address.city, dirtyObj);
    }

    if (address.county) {
      setValue('primaryPracticeAddress.county', address.county, dirtyObj);
    } else {
      setValue('primaryPracticeAddress.county', '', dirtyObj);
    }

    if (address.state) {
      setValue('primaryPracticeAddress.state', address.state, dirtyObj);
    }

    if (address.zipCode) {
      setValue('primaryPracticeAddress.zipCode', address.zipCode, dirtyObj);
    }
  };

  const onSubmit: SubmitHandler<YourInformationForm> = async (formData) => {
    let isError = false;
    let errorMessage = 'Missing required questions';
    const exclude: string[] = ['professionalDesignation', 'line2', 'county'];

    // Primary Practice Location must be in Primary Practice State
    if (
      formData.policyContractState &&
      (formData.primaryPracticeAddress as { state?: string | null } | null)
        ?.state &&
      (formData.primaryPracticeAddress as { state?: string | null }).state !==
        formData.policyContractState
    ) {
      setError('primaryPracticeAddress.state', {
        type: 'server',
        message: '',
      });
      setError('policyContractState', {
        type: 'server',
        message: '',
      });
      isError = true;
      errorMessage =
        'Primary Practice Location must be in Primary Practice State';
    }

    // Check if any required fields are missing values
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Object.entries loses value types
    (Object.entries(formData) as [keyof YourInformationForm, any][]).forEach(
      ([key, value]) => {
        if (value !== null && typeof value === 'object') {
          Object.entries(value).forEach(([nestedKey]) => {
            if (
              isRequired(key) &&
              typeof formData[key as keyof YourInformationForm] === 'object' &&
              !(formData[key] as Record<string, unknown> | null)?.[nestedKey]
            ) {
              if (
                key !== 'professionalDesignation' &&
                !exclude.includes(key) &&
                !exclude.includes(nestedKey)
              ) {
                // skip professionalDesignation;
                setError(
                  `${key}.${nestedKey}` as Parameters<typeof setError>[0],
                  {
                    type: 'server',
                    message: '',
                  }
                );
                isError = true;
                errorMessage = 'Missing required questions';
              }
            }
          });
        }
      }
    );

    (Object.keys(formData) as (keyof YourInformationForm)[]).forEach((key) => {
      if (
        isRequired(key) &&
        !formData[key as keyof YourInformationForm] &&
        !exclude.includes(key)
      ) {
        setError(key, {
          type: 'server',
          message: '',
        });
        isError = true;
        errorMessage = 'Missing required questions';
      }
    });

    const isAnyDesignationSelected = Object.values(
      formData.professionalDesignation
    ).some((key) => key);

    if (!isAnyDesignationSelected) {
      setError('professionalDesignation', {
        type: 'server',
        message: '',
      });
      isError = true;
      errorMessage = 'Missing required questions';
    }

    if (formData.associationCode?.trim()) {
      if (!canValidateAssociationCode) {
        setError('associationCode', {
          type: 'server',
          message:
            'Select a primary practice state before validating an association code.',
        });
        isError = true;
        errorMessage =
          'Please enter a valid association code or clear the field';
      } else {
        const isAssociationCodeValid = await validateAssociationCodeValue(
          formData.associationCode
        );

        if (!isAssociationCodeValid) {
          isError = true;
          errorMessage =
            'Please enter a valid association code or clear the field';
        }
      }
    }

    if (isError) {
      // Popup
      toast({
        title: 'Error',
        description: (
          <pre className="mt-2 w-[340px] rounded-md bg-slate-950 p-4">
            <code className="flex w-full whitespace-normal text-white">
              {errorMessage}
            </code>
          </pre>
        ),
      });
    } else {
      saveAnswers.mutate({
        body: {
          accountId: pk || null,
          pages: {
            ...data?.pages,
            yourInformation: detailsFrontToBack(formData),
          },
        },
      });
    }
  };

  const clearErrorsAndSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    clearErrors();
    handleSubmit(onSubmit)(e);
  };

  return (
    <>
      <AgentAuthorityContext.Provider
        value={{ responsive: true, editMode: true }}
      >
        <Form {...form}>
          <form id="yourInformation" onSubmit={clearErrorsAndSubmit}>
            <Card className="container mb-6">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Please Tell Us About Yourself</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <div className="flex flex-col space-y-2">
                    <Input
                      control={control}
                      label="Agency"
                      placeholder="Type here"
                      name="agencyName"
                      orientation="horizontal"
                      overwriteLabelWidth="w-1/2"
                      overwriteInputWidth="w-1/2"
                      readOnly
                      required
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <Select
                    options={selectableStates}
                    control={control}
                    name="policyContractState"
                    placeholder="Select an Option"
                    label="Primary Practice State"
                    orientation="horizontal"
                    required
                    overwriteLabelWidth="w-1/2"
                    overwriteInputWidth="w-1/2"
                    onChange={(newState) => {
                      const code = (watchAssociationCode || '')
                        .trim()
                        .toUpperCase();
                      setValidatedAssociationCode(null);
                      clearErrors('associationCode');
                      if (!newState) return true;

                      if (!code) {
                        revertAgencyDisplay(newState);
                      } else {
                        void validateAssociationCodeValue(code, {
                          practiceStateOverride: newState,
                        });
                      }
                      return true;
                    }}
                  />
                </div>
                <div className="mb-4">
                  <FormField
                    control={control}
                    name="associationCode"
                    rules={{
                      pattern: {
                        value: /^[A-Za-z0-9]*$/,
                        message:
                          'Association code must contain only letters and numbers.',
                      },
                      maxLength: {
                        value: 5,
                        message: 'Association code must be 1 to 5 characters.',
                      },
                    }}
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-4 max-md:w-full max-md:flex-col">
                        <FormLabel className="w-1/2 font-semibold leading-[1.5rem] max-md:w-full">
                          Association Code{' '}
                          <span className="text-brand-400">(Optional)</span>
                        </FormLabel>
                        <div className="!mt-0 w-1/2 max-md:w-full">
                          <FormControl>
                            <UIInput
                              {...field}
                              value={field.value || ''}
                              placeholder="Type here"
                              maxLength={5}
                              autoCapitalize="characters"
                              onChange={(event) => {
                                const nextValue =
                                  event.target.value.toUpperCase();

                                field.onChange(nextValue);
                                clearErrors('associationCode');

                                if (
                                  validatedAssociationCode &&
                                  validatedAssociationCode.code !== nextValue
                                ) {
                                  setValidatedAssociationCode(null);
                                }
                              }}
                              onBlur={async (event) => {
                                const nextValue = event.target.value
                                  .trim()
                                  .toUpperCase();

                                field.onBlur();

                                if (nextValue !== field.value) {
                                  setValue('associationCode', nextValue, {
                                    shouldDirty: true,
                                    shouldTouch: true,
                                    shouldValidate: true,
                                  });
                                }

                                if (!nextValue) {
                                  clearErrors('associationCode');
                                  setValidatedAssociationCode(null);
                                  revertAgencyDisplay(watchPolicyContractState);
                                  return;
                                }

                                if (!canValidateAssociationCode) {
                                  return;
                                }

                                await validateAssociationCodeValue(nextValue);
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                          {watchAssociationCode &&
                          !canValidateAssociationCode ? (
                            <p className="mt-2 text-sm text-brand-500">
                              Select a primary practice state to validate an
                              association code.
                            </p>
                          ) : null}
                          {validatedAssociationCode?.code ===
                          watchAssociationCode ? (
                            <p className="mt-2 text-sm text-green-700">
                              Association code applied for{' '}
                              {validatedAssociationCode?.agencyName}.
                            </p>
                          ) : null}
                        </div>
                      </FormItem>
                    )}
                  />
                </div>

                <div className="mb-4">
                  <Input
                    control={control}
                    label="First Name"
                    placeholder="Type here"
                    name="firstName"
                    orientation="horizontal"
                    required
                    overwriteLabelWidth="w-1/2"
                    overwriteInputWidth="w-1/2"
                    trim
                  />
                </div>
                <div className="mb-4">
                  <Input
                    control={control}
                    label={
                      <>
                        Middle Name{' '}
                        <span className="text-brand-400">(Optional)</span>
                      </>
                    }
                    placeholder="Type here"
                    name="middleName"
                    orientation="horizontal"
                    required={false}
                    overwriteLabelWidth="w-1/2"
                    overwriteInputWidth="w-1/2"
                    trim
                  />
                </div>
                <div className="mb-4">
                  <Input
                    control={control}
                    label="Last Name"
                    placeholder="Type here"
                    name="lastName"
                    orientation="horizontal"
                    required
                    overwriteLabelWidth="w-1/2"
                    overwriteInputWidth="w-1/2"
                    trim
                  />
                </div>
                <div className="mb-4 flex w-full flex-row gap-4 font-normal max-md:flex-col">
                  <span className="w-1/2 text-sm font-semibold leading-[1.5rem]">
                    <span
                      className={`font-semibold ${errors.professionalDesignation ? 'text-red-500' : ''}`}
                    >
                      Designations
                      <span className="text-red-400">*</span>
                    </span>
                  </span>
                  <div className="flex flex-row flex-wrap md:w-1/2">
                    <Checkbox
                      control={control}
                      label="MD"
                      name="professionalDesignation.MD"
                      direction="reverse"
                    />
                    <Checkbox
                      control={control}
                      label="DDS"
                      name="professionalDesignation.DDS"
                      direction="reverse"
                    />
                    <Checkbox
                      control={control}
                      label="DMD"
                      name="professionalDesignation.DMD"
                      direction="reverse"
                    />
                    <Checkbox
                      control={control}
                      label="BDS"
                      name="professionalDesignation.BDS"
                      direction="reverse"
                    />
                    <Checkbox
                      control={control}
                      label="MS"
                      name="professionalDesignation.MS"
                      direction="reverse"
                    />
                    <Checkbox
                      control={control}
                      label="MSD"
                      name="professionalDesignation.MSD"
                      direction="reverse"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <DateField
                    control={control}
                    name="dateOfBirth"
                    label="Date of Birth"
                    orientation="horizontal"
                    required
                    overwriteLabelWidth="w-1/2"
                    overwriteInputWidth="w-1/2"
                  />
                </div>
                <div className="mb-4">
                  <Input
                    control={control}
                    label="Email Address - Personal Email Address"
                    placeholder="Type here"
                    name="emailAddress"
                    orientation="horizontal"
                    required
                    overwriteLabelWidth="w-1/2"
                    overwriteInputWidth="w-1/2"
                    type="email"
                    pattern=".+@.+\..+"
                  />
                </div>
                <div className="mb-4">
                  <Input
                    control={control}
                    label={
                      <>
                        Email Address (post graduation) (if different then
                        above){' '}
                        <span className="text-brand-400">(Optional)</span>
                      </>
                    }
                    placeholder="Type here"
                    name="postGraduationEmail"
                    orientation="horizontal"
                    required={false}
                    nativeRequired={false}
                    overwriteLabelWidth="w-1/2"
                    overwriteInputWidth="w-1/2"
                    type="email"
                    pattern=".+@.+\..+"
                  />
                </div>
                <div className="mb-4 flex flex-col space-y-2">
                  <PhoneInput
                    control={control}
                    label="Cell Phone Number"
                    placeholder="Type here"
                    name="cellPhoneNumber"
                    orientation="horizontal"
                    type="tel"
                    required
                    overwriteLabelWidth="w-1/2"
                    overwriteInputWidth="w-1/2"
                  />
                </div>
                <div className="mb-4">
                  <GoogleInput
                    asSearchField
                    defaultValue=""
                    onAddressSelect={(address) => {
                      const dirtyObj = {
                        shouldDirty: true,
                        shouldTouch: true,
                        shouldValidate: true,
                      };

                      if (address.line1) {
                        setValue(
                          'mailingAddress.line1',
                          address.line1,
                          dirtyObj
                        );
                      }

                      if (address.line2) {
                        setValue(
                          'mailingAddress.line2',
                          address.line2,
                          dirtyObj
                        );
                      }

                      if (address.city) {
                        setValue('mailingAddress.city', address.city, dirtyObj);
                      }

                      setValue(
                        'mailingAddress.county',
                        address.county || '',
                        dirtyObj
                      );

                      if (address.state) {
                        setValue(
                          'mailingAddress.state',
                          address.state,
                          dirtyObj
                        );
                      }

                      if (address.zipCode) {
                        setValue(
                          'mailingAddress.zipCode',
                          address.zipCode,
                          dirtyObj
                        );
                      }

                      if (address.formatted) {
                        setValue(
                          'mailingDisplayAddress',
                          address.formatted,
                          dirtyObj
                        );
                      }
                    }}
                    label={
                      <>
                        Search For Mailing Address{' '}
                        <span className="text-brand-400">(Optional)</span>
                      </>
                    }
                    placeholder="Type to search"
                    orientation="horizontal"
                    overwriteLabelWidth="w-1/2"
                    overwriteInputWidth="w-1/2"
                  />
                </div>
                <div className="mb-4 flex gap-4 max-md:flex-col">
                  <div className="md:w-1/2">
                    <Input
                      control={control}
                      label="Street Address"
                      placeholder="Type here"
                      name="mailingAddress.line1"
                      orientation="vertical"
                      required
                      overwriteLabelWidth="w-1/2"
                      overwriteInputWidth="w-1/2"
                    />
                  </div>
                  <div className="md:w-1/2">
                    <Input
                      control={control}
                      label={
                        <>
                          Suite, Floor, Building{' '}
                          <span className="text-brand-400">(Optional)</span>
                        </>
                      }
                      placeholder="Type here"
                      name="mailingAddress.line2"
                      orientation="vertical"
                      required={false}
                      nativeRequired={false}
                      overwriteLabelWidth="w-1/2"
                      overwriteInputWidth="w-1/2"
                    />
                  </div>
                </div>
                <div className="mb-4 flex gap-4 max-md:flex-col">
                  <div className="md:w-1/3">
                    <Input
                      control={control}
                      label="City"
                      placeholder="Type here"
                      name="mailingAddress.city"
                      orientation="vertical"
                      required
                      overwriteLabelWidth="w-1/2"
                      overwriteInputWidth="w-1/2"
                    />
                  </div>
                  <div className="md:w-1/3">
                    <Select
                      options={SelectableUnitedStatesNewGrad}
                      control={control}
                      name="mailingAddress.state"
                      placeholder="Select an Option"
                      label="State"
                      orientation="vertical"
                      required
                      overwriteLabelWidth="w-1/2"
                      overwriteInputWidth="w-1/2"
                    />
                  </div>
                  <div className="md:w-1/3">
                    <ZipInput
                      control={control}
                      label="Zip Code"
                      placeholder="Type here"
                      name="mailingAddress.zipCode"
                      orientation="vertical"
                      required
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <Select
                    options={SelectableYesNo}
                    control={control}
                    name="memberOfSda"
                    placeholder="Select an Option"
                    label="Are you a member of your state dental association or society?"
                    orientation="horizontal"
                    required
                    overwriteLabelWidth="w-1/2"
                    overwriteInputWidth="w-1/2"
                  />
                </div>
                <div className="mb-4">
                  <Input
                    control={control}
                    label="Name of dental school you graduated from or will graduate from?"
                    placeholder="Type here"
                    name="nameOfDentalSchool"
                    orientation="horizontal"
                    required
                    overwriteLabelWidth="w-1/2"
                    overwriteInputWidth="w-1/2"
                  />
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-1/2 font-semibold max-md:hidden">
                    Month/Year of Graduation
                    <span className="text-red-400">*</span>
                  </div>
                  <div className="flex w-full gap-4 max-md:flex-col md:w-1/2">
                    <div className="mb-4 md:w-1/2">
                      <Select
                        options={selectableMonth}
                        control={control}
                        name="yearMonthMonth"
                        placeholder="Select an Option"
                        label="Month of Graduation"
                        orientation="vertical"
                        required
                        overwriteLabelWidth="w-1/2"
                        overwriteInputWidth="w-1/2"
                      />
                    </div>
                    <div className="mb-4 md:w-1/2">
                      <Input
                        control={control}
                        label="Year of Graduation"
                        placeholder="Type here"
                        name="yearMonthYear"
                        orientation="vertical"
                        required
                        overwriteLabelWidth="w-1/2"
                        overwriteInputWidth="w-1/2"
                        type="number"
                        pattern="^\d{4}$"
                        min={new Date().getFullYear() - 2}
                        max={new Date().getFullYear() + 2}
                      />
                    </div>
                  </div>
                </div>
                <div className="mb-4">
                  <Select
                    options={SelectablePracticeSpecialty}
                    control={control}
                    name="practiceSpecialty"
                    placeholder="Select an Option"
                    label="Specialty"
                    orientation="horizontal"
                    required
                    overwriteLabelWidth="w-1/2"
                    overwriteInputWidth="w-1/2"
                  />
                </div>
              </CardContent>
            </Card>
            <Card className="container mb-6">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>
                  Primary Practice Location (If Unsure, Enter Your Address In
                  The State Where You Will Primarily Practice)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <GoogleInput
                    asSearchField
                    defaultValue=""
                    onAddressSelect={handleSelectPracticeAddress}
                    label={
                      <>
                        Search For Primary Practice Address{' '}
                        <span className="text-brand-400">(Optional)</span>
                      </>
                    }
                    placeholder="Type to search"
                    orientation="horizontal"
                    overwriteLabelWidth="w-1/2"
                    overwriteInputWidth="w-1/2"
                  />
                </div>
                <div className="mb-4">
                  <Input
                    control={control}
                    label="Street Address"
                    placeholder="Type here"
                    name="primaryPracticeAddress.line1"
                    orientation="vertical"
                    required
                    overwriteLabelWidth="w-1/2"
                    overwriteInputWidth="w-1/2"
                  />
                </div>
                <div className="mb-4">
                  <Input
                    control={control}
                    label={
                      <>
                        Suite, Floor, Building{' '}
                        <span className="text-brand-400">(Optional)</span>
                      </>
                    }
                    placeholder="Type here"
                    name="primaryPracticeAddress.line2"
                    orientation="vertical"
                    required={false}
                    nativeRequired={false}
                    overwriteLabelWidth="w-1/2"
                    overwriteInputWidth="w-1/2"
                  />
                </div>
                <div className="mb-4 flex gap-4 max-md:flex-col">
                  <div className="md:w-1/4">
                    <Input
                      control={control}
                      label="City"
                      placeholder="Type here"
                      name="primaryPracticeAddress.city"
                      orientation="vertical"
                      required
                      overwriteLabelWidth="w-1/2"
                      overwriteInputWidth="w-1/2"
                    />
                  </div>
                  <div className="md:w-1/4">
                    <Select
                      options={SelectableUnitedStatesNewGrad}
                      control={control}
                      name="primaryPracticeAddress.state"
                      placeholder="Select an Option"
                      label="State"
                      orientation="vertical"
                      required
                      overwriteLabelWidth="w-1/2"
                      overwriteInputWidth="w-1/2"
                    />
                  </div>
                  <div className="md:w-1/4">
                    <FormField
                      control={control}
                      name="primaryPracticeAddress.county"
                      rules={{
                        pattern: {
                          value: /^[^/@0-9]+$/,
                          message:
                            'County cannot contain numbers, @ or / characters',
                        },
                        maxLength: {
                          value: 100,
                          message: 'County must be 100 characters or less',
                        },
                      }}
                      render={({ field }) => (
                        <FormItem className="flex w-full flex-col">
                          <FormLabel className="mb-2 font-semibold">
                            County
                          </FormLabel>
                          <FormControl>
                            <UIInput
                              placeholder="Type here"
                              {...field}
                              value={field.value || ''}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="md:w-1/4">
                    <ZipInput
                      control={control}
                      label="Zip Code"
                      placeholder="Type here"
                      name="primaryPracticeAddress.zipCode"
                      orientation="vertical"
                      required
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="container">
              {watchFirstTimePractice !== YesNoEnum.NO ? (
                <div className="bg-[#009BA70D] p-4">
                  <p className="text-[#009BA7]">
                    <Info className="mr-2 inline h-5 w-5" />
                    <span>
                      Coverage is only available for individuals who are now, or
                      will be in the next 6 months, entering private dental
                      practice for the first time.
                    </span>
                  </p>
                </div>
              ) : null}
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>
                  Are you entering in practice for the first time?
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <Select
                    options={SelectableYesNo}
                    control={control}
                    name="firstTimePractice"
                    placeholder="Select an Option"
                    label="Are you entering in practice for the first time?"
                    orientation="horizontal"
                    required
                    overwriteLabelWidth="w-1/2"
                    overwriteInputWidth="w-1/2"
                  />
                </div>
              </CardContent>
              {watchFirstTimePractice === YesNoEnum.NO ? (
                <div className="bg-[#CA4F500D] p-4">
                  <p className="text-[#CA4F50]">
                    <Info className="mr-2 inline h-5 w-5" />
                    <span>
                      Sorry for the confusion, but insurance is only available
                      to individuals who are now or in the next 6 months
                      starting a private dental practice for the first time.
                      Hope to see you soon as we are working and expanding our
                      services to cover all your needs.
                    </span>
                  </p>
                </div>
              ) : null}
            </Card>
          </form>
        </Form>
        <div className="mt-4 flex items-center justify-end">
          <Button
            type="submit"
            form="yourInformation"
            disabled={watchFirstTimePractice === YesNoEnum.NO}
            className={`rounded-md bg-brand-green px-4 py-2 text-white hover:bg-brand-600 ${watchFirstTimePractice === YesNoEnum.NO ? 'cursor-not-allowed opacity-50' : ''}`}
            loading={saveAnswers.isPending || validateAssociationCode.isPending}
          >
            Next
          </Button>
        </div>
      </AgentAuthorityContext.Provider>
    </>
  );
};

export default NewGrad;
